// Performance monitoring utilities

export interface PerformanceMetrics {
  loadTime: number;
  domContentLoaded: number;
  firstContentfulPaint?: number;
  largestContentfulPaint?: number;
  cumulativeLayoutShift?: number;
  firstInputDelay?: number;
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: PerformanceMetrics = {
    loadTime: 0,
    domContentLoaded: 0,
  };
  private reportLogged: boolean = false;

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // Initialize performance monitoring
  init() {
    if (typeof window === "undefined") return;

    // Measure basic load times
    window.addEventListener("load", () => {
      const navigation = performance.getEntriesByType(
        "navigation"
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        this.metrics.loadTime = navigation.loadEventEnd - navigation.fetchStart;
        this.metrics.domContentLoaded =
          navigation.domContentLoadedEventEnd - navigation.fetchStart;
      }
    });

    // Measure Web Vitals if available
    this.measureWebVitals();

    // Log performance warnings
    this.checkPerformanceThresholds();
  }

  private measureWebVitals() {
    if (typeof window === "undefined" || !("PerformanceObserver" in window))
      return;

    try {
      // First Contentful Paint
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const fcp = entries.find(
          (entry) => entry.name === "first-contentful-paint"
        );
        if (fcp) {
          this.metrics.firstContentfulPaint = fcp.startTime;
        }
      });
      fcpObserver.observe({ entryTypes: ["paint"] });

      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          this.metrics.largestContentfulPaint = lastEntry.startTime;
        }
      });
      lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });

      // Cumulative Layout Shift
      const clsObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        this.metrics.cumulativeLayoutShift = clsValue;
      });
      clsObserver.observe({ entryTypes: ["layout-shift"] });

      // First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstInput = entries[0];
        if (firstInput) {
          this.metrics.firstInputDelay =
            (firstInput as any).processingStart - firstInput.startTime;
        }
      });
      fidObserver.observe({ entryTypes: ["first-input"] });
    } catch (error) {
      console.warn("Web Vitals measurement failed:", error);
    }
  }

  private checkPerformanceThresholds() {
    setTimeout(() => {
      const { loadTime, firstContentfulPaint, largestContentfulPaint } =
        this.metrics;

      // Check if page loads within 3 seconds (3000ms)
      if (loadTime > 3000) {
        console.warn(
          `Page load time (${loadTime}ms) exceeds 3 second threshold`
        );
      }

      if (firstContentfulPaint && firstContentfulPaint > 1800) {
        console.warn(
          `First Contentful Paint (${firstContentfulPaint}ms) is slow`
        );
      }

      if (largestContentfulPaint && largestContentfulPaint > 2500) {
        console.warn(
          `Largest Contentful Paint (${largestContentfulPaint}ms) is slow`
        );
      }
    }, 5000); // Check after 5 seconds to ensure all metrics are captured
  }

  // Get current metrics
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  // Log performance report
  logReport() {
    if (typeof window === "undefined" || this.reportLogged) return;

    this.reportLogged = true;

    console.group("Performance Report");
    console.log("Load Time:", this.metrics.loadTime + "ms");
    console.log("DOM Content Loaded:", this.metrics.domContentLoaded + "ms");

    if (this.metrics.firstContentfulPaint) {
      console.log(
        "First Contentful Paint:",
        this.metrics.firstContentfulPaint + "ms"
      );
    }

    if (this.metrics.largestContentfulPaint) {
      console.log(
        "Largest Contentful Paint:",
        this.metrics.largestContentfulPaint + "ms"
      );
    }

    if (this.metrics.cumulativeLayoutShift !== undefined) {
      console.log(
        "Cumulative Layout Shift:",
        this.metrics.cumulativeLayoutShift
      );
    }

    if (this.metrics.firstInputDelay) {
      console.log("First Input Delay:", this.metrics.firstInputDelay + "ms");
    }

    console.groupEnd();
  }
}

// Resource loading optimization utilities
export function preloadResource(href: string, as: string, type?: string) {
  if (typeof document === "undefined") return;

  const link = document.createElement("link");
  link.rel = "preload";
  link.href = href;
  link.as = as;
  if (type) link.type = type;

  document.head.appendChild(link);
}

export function preloadImage(src: string) {
  if (typeof window === "undefined") return;

  const img = new Image();
  img.src = src;
}

// Debounce utility for performance optimization
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle utility for performance optimization
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
